// react
import React, { useMemo } from 'react';
import { useDispatch } from 'react-redux';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { createFileRoute, Link, useParams } from '@tanstack/react-router';

// material ui icons
import {
  BoltRounded as BoltRoundedIcon,
  LocalFireDepartmentRounded,
  WaterDropRounded,
} from '@mui/icons-material';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import PendingRoundedIcon from '@mui/icons-material/PendingRounded';

// material ui
import {
  alpha,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';

// api
import {
  useCreatePortfolioEnergyGroupRequestMutation,
  useDeletePortfolioEnergyGroupRequestMutation,
  useGetPortfolioEnergyGroupRequestsQuery,
} from '@/api/energy-analysis/portfolioEnergyGroupSlice';
import { useUpdatePortfolioAnalysisNowUpdatePortfolioAnalysisNowAccountIdPutMutation } from '@/api/generated/energyAnalysis/endpoints/portfolioAnalysis';
import {
  useCreateSubscriptionSubscriptionPostMutation,
  useGetSubscriptionSubscriptionAccountIdGetQuery,
  useUpdateSubscriptionSubscriptionAccountIdPatchMutation,
} from '@/api/generated/energyAnalysis/endpoints/portfolioAnalysisSubscription';
import {
  useDeleteEnergyGroupMutation,
  useGetEnergyGroupsQuery,
} from '@/api/generated/recalc/endpoints/v2/energyGroups';

// shared components
import CustomSwitch from '@/components/ui/switch/CustomSwitch';

// feature-components
import ServiceActivationSwitch from '@/features/service-manager/components/ServiceActivationSwitch';

// components
import { useAlertProvider } from '@/hooks/useAlertProvider';
import { showSnackbar } from '@/redux/snackbarSlice';

import { DateTime } from 'luxon';

const energyTypes: EnergyType[] = [
  {
    key: 'Power',
    label: 'Power',
    serviceId: 7,
    icon: BoltRoundedIcon,
    color: 'power.main',
    description: 'Monitor and analyze electrical consumption',
  },
  {
    key: 'Heat',
    label: 'Heat',
    serviceId: 9,
    icon: LocalFireDepartmentRounded,
    color: 'heat.main',
    description: 'Track heating energy usage',
  },
  {
    key: 'Water',
    label: 'Water',
    serviceId: 8,
    icon: WaterDropRounded,
    color: 'water.main',
    description: 'Monitor water consumption',
  },
];

const ENERGY_TYPES = [
  { type: 'power', label: 'Power' },
  { type: 'water', label: 'Water' },
  { type: 'heat', label: 'Heat' },
];

const energyTypeMap = energyTypes.reduce(
  (acc, type) => {
    acc[type.key.toLowerCase()] = type;
    return acc;
  },
  {} as Record<string, EnergyType>,
);

interface EnergyType {
  key: string;
  label: string;
  serviceId: number;
  icon: typeof BoltRoundedIcon; // Update with actual icon types
  color: string;
  description: string;
}

const hasStatus = (error: unknown): error is { status?: number | string } => {
  return typeof error === 'object' && error !== null && 'status' in error;
};

const hasErrorData = (
  error: unknown,
): error is { data?: { detail?: { ErrorMessage?: string } } } => {
  return typeof error === 'object' && error !== null && 'data' in error;
};

const getErrorMessage = (error: unknown): string | undefined => {
  if (!hasErrorData(error) || !error.data) {
    return undefined;
  }

  return error.data.detail?.ErrorMessage;
};

const renderStatusChip = (status: string, timestamp?: string) => {
  const formattedTimestamp = timestamp
    ? DateTime.fromISO(timestamp).toLocaleString(DateTime.DATETIME_MED)
    : 'N/A';

  switch (status) {
    case 'Pending':
      return (
        <Chip
          variant="outlined"
          icon={<PendingRoundedIcon />}
          sx={{
            borderRadius: 2,
            fontSize: '0.75rem',
            borderColor: (theme) => theme.palette.warning.main,
            minWidth: 90,
            backgroundColor: (theme) => alpha(theme.palette.warning.main, 0.15),
            color: (theme) => theme.palette.warning.main,
            '& .MuiChip-icon': {
              color: (theme) => theme.palette.warning.main,
            },
          }}
          label={formattedTimestamp}
          size="small"
        />
      );
    case 'InProgress':
      return (
        <Chip
          variant="outlined"
          icon={<PendingRoundedIcon />}
          sx={{
            borderRadius: 2,
            fontSize: '0.75rem',
            borderColor: (theme) => theme.palette.info.main,
            minWidth: 90,
            backgroundColor: (theme) => alpha(theme.palette.info.main, 0.15),
            color: (theme) => theme.palette.info.main,
            '& .MuiChip-icon': {
              color: (theme) => theme.palette.info.main,
            },
          }}
          label={formattedTimestamp}
          size="small"
        />
      );
    case 'Completed':
      return (
        <Chip
          variant="outlined"
          icon={<CheckCircleRoundedIcon />}
          sx={{
            borderRadius: 2,
            borderColor: (theme) => theme.palette.success.main,
            fontSize: '0.75rem',
            minWidth: 90,
            backgroundColor: (theme) => alpha(theme.palette.success.main, 0.15),
            color: (theme) => theme.palette.success.main,
            '& .MuiChip-icon': {
              color: (theme) => theme.palette.success.main,
            },
          }}
          label={formattedTimestamp}
          size="small"
        />
      );
    case 'Failed':
      return (
        <Chip
          variant="outlined"
          icon={<ErrorRoundedIcon />}
          sx={{
            borderRadius: 2,
            fontSize: '0.75rem',
            borderColor: (theme) => theme.palette.error.main,
            minWidth: 90,
            backgroundColor: (theme) => alpha(theme.palette.error.main, 0.15),
            color: (theme) => theme.palette.error.main,
            '& .MuiChip-icon': {
              color: (theme) => theme.palette.error.main,
            },
          }}
          label={formattedTimestamp}
          size="small"
        />
      );
    default:
      return null;
  }
};

export const Route = createFileRoute(
  '/$accountId/_admin/service-manager/$managedAccountId/portfolio-analysis',
)({
  component: RouteComponent,
  staticData: { crumb: 'service-manager:services.portfolioAnalysis.title' },
});

function RouteComponent() {
  const { managedAccountId } = useParams({
    from: '/$accountId/_admin/service-manager/$managedAccountId/portfolio-analysis',
  });
  const managedAccountIdNumber = Number(managedAccountId);
  const hasManagedAccountId = Number.isFinite(managedAccountIdNumber);

  const { t } = useTranslation('service-manager');

  const defaultSubscription = {
    account_id: managedAccountIdNumber,
    water: false,
    heat: false,
    power: true,
    default_daytime_start: 8,
    default_daytime_end: 16,
    demo_account: false,
  };

  const dispatch = useDispatch();

  const { data: energyGroups } = useGetEnergyGroupsQuery(
    { accountIds: [managedAccountIdNumber] },
    { skip: !hasManagedAccountId },
  );

  const [deleteEnergyGroup] = useDeleteEnergyGroupMutation();

  const [updatePortfolioAnalysisSubscription] =
    useUpdateSubscriptionSubscriptionAccountIdPatchMutation();

  const [updatePortfolioAnalysisNow] =
    useUpdatePortfolioAnalysisNowUpdatePortfolioAnalysisNowAccountIdPutMutation();

  const handleManualRun = async () => {
    const result = await updatePortfolioAnalysisNow({
      accountId: managedAccountIdNumber,
    });

    if (!('error' in result)) {
      dispatch(
        showSnackbar({
          message: t('portfolioAnalysis.messages.jobStarting'),
          severity: 'success',
        }),
      );
    }
    // Error handled by apiErrorMiddleware (snackbar + App Insights).
  };

  const [
    postPortfolioAnalysisSubscriptionMutation,
    { isLoading: isPortfolioAnalysisPostLoading },
  ] = useCreateSubscriptionSubscriptionPostMutation();

  const { currentData: subscription, error: subscriptionError } =
    useGetSubscriptionSubscriptionAccountIdGetQuery(
      { accountId: managedAccountIdNumber },
      {
        skip: !hasManagedAccountId, // Skip fetching if no valid account ID or service is not available
      },
    );

  const { data: requests } = useGetPortfolioEnergyGroupRequestsQuery(
    { account_id: managedAccountIdNumber },
    { skip: !hasManagedAccountId },
  );

  const [createRequest, { isLoading: isCreating }] =
    useCreatePortfolioEnergyGroupRequestMutation();
  const [deleteRequest, { isLoading: isDeleting }] =
    useDeletePortfolioEnergyGroupRequestMutation();

  const statusCode = hasStatus(subscriptionError)
    ? subscriptionError.status
    : undefined;
  const is404 = statusCode === 404;
  const is500 = statusCode === 500;
  const serverErrorMessage = getErrorMessage(subscriptionError);

  const getMeterCount = (type: 'power' | 'heat' | 'water') => {
    if (!subscription) return 0;
    const count =
      subscription[`num_${type}_meters` as keyof typeof subscription];
    return typeof count === 'number' ? count : 0;
  };

  const handleCreatePortfolioTree = async (type) => {
    const response = await createRequest({
      AccountId: managedAccountIdNumber,
      Type: type,
    });

    if ('error' in response) {
      // Error handled by apiErrorMiddleware (snackbar + App Insights).
      return;
    }

    dispatch(
      showSnackbar({
        message: t('portfolioEnergyTree.createSuccess', {
          defaultValue: `${type} request created successfully`,
          type: type,
        }),
        severity: 'success',
      }),
    );
  };

  const handleDeletePortfolioTree = async (type) => {
    if (
      !window.confirm(
        t('portfolioEnergyTree.confirmDelete', {
          defaultValue: `Are you sure you want to delete the ${type} portfolio tree and request?`,
          type: type,
        }),
      )
    ) {
      return;
    }
    const portfolioTree = energyGroups.find(
      (eg) => eg.type?.toLowerCase() === `portfolio${type.toLowerCase()}`,
    );
    if (!portfolioTree) {
      dispatch(
        showSnackbar({
          message: t('portfolioEnergyTree.deleteNoTreeError', {
            defaultValue: `No ${type} portfolio tree found to delete`,
            type: type,
          }),
          severity: 'warning',
        }),
      );
    } else {
      const treeResponse = await deleteEnergyGroup({
        groupId: portfolioTree.energyGroupId,
      });

      if ('error' in treeResponse) {
        // Error handled by apiErrorMiddleware (snackbar + App Insights).
        return;
      }

      dispatch(
        showSnackbar({
          message: t('portfolioEnergyTree.deleteGroupSuccess', {
            defaultValue: `${type} portfolio tree deleted successfully`,
            type: type,
          }),
          severity: 'success',
        }),
      );
    }

    const requestResponse = await deleteRequest({
      account_id: managedAccountIdNumber,
      analysis_type: type.toLowerCase(),
    });

    if ('error' in requestResponse) {
      // Error handled by apiErrorMiddleware (snackbar + App Insights).
      return;
    }

    dispatch(
      showSnackbar({
        message: t('portfolioEnergyTree.deleteSuccess', {
          defaultValue: `${type} request deleted successfully`,
          type: type,
        }),
        severity: 'success',
      }),
    );
  };

  const handleCreateSubscription = async () => {
    const result = await postPortfolioAnalysisSubscriptionMutation({
      portfolioAnalysisSubscriptionInput: defaultSubscription,
    });

    if (!('error' in result)) {
      dispatch(
        showSnackbar({
          message: t('portfolioAnalysis.messages.subscriptionCreated'),
          severity: 'success',
        }),
      );
    }
    // Error handled by apiErrorMiddleware (snackbar + App Insights).
  };

  const handleUpdateSubscription = async (type: 'power' | 'heat' | 'water') => {
    if (!subscription || !hasManagedAccountId) {
      return;
    }

    const typeLabel = energyTypeMap[type]?.label ?? type;

    const update = {
      ...subscription,
      [type]: !subscription[type],
    };

    const result = await updatePortfolioAnalysisSubscription({
      accountId: managedAccountIdNumber,
      portfolioAnalysisSubscriptionPatchInput: update,
    });

    if (!('error' in result)) {
      dispatch(
        showSnackbar({
          message: t('portfolioAnalysis.messages.subscriptionUpdated', {
            type: typeLabel,
            defaultValue: `${typeLabel} subscription updated successfully`,
          }),
          severity: 'success',
        }),
      );
    }
    // Error handled by apiErrorMiddleware (snackbar + App Insights).
  };

  const requestsByType = useMemo(() => {
    if (!requests) return {};

    const grouped = {};
    ENERGY_TYPES.forEach(({ type }) => {
      const typeRequests = requests.filter(
        (r) => r.Type?.toLowerCase() === type.toLowerCase(),
      );
      const latestRequest = typeRequests.sort(
        (a, b) =>
          new Date(b.ChangedTimestamp as string).getTime() -
          new Date(a.ChangedTimestamp as string).getTime(),
      )[0];

      grouped[type] = {
        latest: latestRequest,
        hasRequest: Boolean(typeRequests.length),
      };
    });

    return grouped;
  }, [requests]);

  useAlertProvider({
    enabled: is404,
    severity: 'warning',
    message: t('portfolioAnalysis.noSubscription.description'),
    action: (
      <Button
        size="small"
        variant="outlined"
        onClick={handleCreateSubscription}
        disabled={isPortfolioAnalysisPostLoading}
      >
        {isPortfolioAnalysisPostLoading
          ? t('portfolioAnalysis.noSubscription.creating')
          : t('portfolioAnalysis.noSubscription.createButton')}
      </Button>
    ),
  });

  useAlertProvider({
    enabled: is500,
    severity: 'error',
    message: serverErrorMessage || t('basicEnergy.unexpected_error'),
  });

  const renderSubscriptionTable = () => {
    if (!subscription) return null;

    return (
      <Stack spacing={2}>
        <Table aria-label="portfolio analysis resources">
          <TableHead>
            <TableRow>
              <TableCell>
                <Typography variant="subtitle2" fontWeight="bold">
                  {t('portfolioAnalysis.table.type')}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography
                  align="center"
                  variant="subtitle2"
                  fontWeight="bold"
                >
                  {t('portfolioAnalysis.table.meterCount')}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography
                  align="center"
                  variant="subtitle2"
                  fontWeight="bold"
                >
                  {t('portfolioAnalysis.table.status')}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography
                  align="center"
                  variant="subtitle2"
                  fontWeight="bold"
                >
                  {t('portfolioAnalysis.table.actions')}
                </Typography>
              </TableCell>
              <TableCell align="center">
                <Typography variant="subtitle2" fontWeight="bold">
                  {t('portfolioAnalysis.table.included')}
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {['power', 'heat', 'water'].map((type) => {
              return (
                <TableRow
                  key={type}
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                >
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Box>
                        {React.createElement(energyTypeMap[type]?.icon, {
                          sx: {
                            color: energyTypeMap[type]?.color,
                            fontSize: 24,
                          },
                        })}
                      </Box>
                      <Box>
                        <Typography variant="body1" fontWeight="500">
                          {energyTypeMap[type]?.label}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {energyTypeMap[type]?.description}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2" color="text.secondary">
                      {getMeterCount(type as 'power' | 'heat' | 'water')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {requestsByType[type]?.latest && (
                      <Box display="flex" justifyContent="center">
                        <Tooltip
                          placement="top"
                          title={
                            <Box>
                              <Typography variant="caption" display="block">
                                <strong>{t('common.status')}</strong>{' '}
                                {requestsByType[type]?.latest.Status}
                              </Typography>
                            </Box>
                          }
                        >
                          <Box display="flex" alignItems="center">
                            {renderStatusChip(
                              requestsByType[type]?.latest.Status,
                              requestsByType[type]?.latest.ChangedTimestamp,
                            )}
                          </Box>
                        </Tooltip>
                      </Box>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Button
                      color={
                        requestsByType[type]?.hasRequest ? 'error' : 'primary'
                      }
                      disabled={isCreating || isDeleting}
                      onClick={() =>
                        requestsByType[type]?.hasRequest
                          ? handleDeletePortfolioTree(type)
                          : handleCreatePortfolioTree(type)
                      }
                    >
                      {requestsByType[type]?.hasRequest
                        ? t('portfolioAnalysis.actions.deleteTree')
                        : t('portfolioAnalysis.actions.createTree')}
                    </Button>
                  </TableCell>
                  <TableCell align="center">
                    <CustomSwitch
                      checked={subscription?.[type] || false}
                      onChange={() =>
                        handleUpdateSubscription(
                          type as 'power' | 'heat' | 'water',
                        )
                      }
                      inputProps={{ 'aria-label': `toggle ${type}` }}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Stack>
    );
  };

  return (
    <Stack spacing={2}>
      <Card>
        <CardHeader
          title={t('services.portfolioAnalysis.title')}
          action={
            <Box display="flex" alignItems="center" gap={1}>
              <ServiceActivationSwitch
                managedAccountId={managedAccountId}
                servicePath="portfolio-analysis"
                ariaLabel={t('services.portfolioAnalysis.title')}
              />
            </Box>
          }
          slotProps={{
            title: { variant: 'h6', fontWeight: 600 }
          }}
        />
        <Divider />
        <CardContent>
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              {t('portfolioAnalysis.description')}
            </Typography>
            {renderSubscriptionTable()}
          </Stack>
        </CardContent>
        <Divider />
        <CardActions>
          <Box
            sx={{
              gap: 1,
              display: 'flex',
              justifyContent: 'flex-end',
              width: '100%',
            }}
          >
            <Button
              variant="outlined"
              size="small"
              onClick={handleManualRun}
              disabled={!subscription}
              sx={{
                ':hover': {
                  backgroundColor: 'primary.main',
                  color: 'primary.contrastText',
                },
              }}
            >
              {t('portfolioAnalysis.runJob')}
            </Button>
            <Button
              variant="outlined"
              component={Link}
              to={`/${managedAccountId}/operations/portfolio-analysis`}
              size="small"
              color="primary"
              disableElevation
              sx={{
                ':hover': {
                  backgroundColor: 'primary.main',
                  color: 'primary.contrastText',
                },
              }}
            >
              {t('portfolioAnalysis.operationalStatus')}
            </Button>
          </Box>
        </CardActions>
      </Card>
    </Stack>
  );
}
